import { inject, Injectable, InjectionToken } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap } from 'rxjs';

import { AuthTelemetryReporter, transition } from '@ninety/mobile/platform/auth';

import { SessionActions } from './session.actions';
import { selectSession } from './session.selectors';

/**
 * The clock the machine reads.
 *
 * Injectable so deadline behaviour — challenge expiry, authorization response age — can be tested
 * without fake timers or `jest.spyOn(Date, 'now')`.
 */
export const AUTH_CLOCK = new InjectionToken<() => number>('AuthClock', {
  providedIn: 'root',
  factory: () => () => Date.now(),
});

/**
 * Runs the auth machine.
 *
 * One effect, and it is the only place `transition()` is called. It reads the current state, asks
 * `transition()` what happens next, and emits the answer as actions: the new state first, then one
 * `commandIssued` per Command.
 *
 * ## Why this isn't in the reducer
 *
 * `transition()` returns `[AuthState, Command[]]` and a reducer can only return the state. Putting
 * it here keeps both halves usable without stashing a work queue inside the state.
 *
 * ## Ordering
 *
 * `stateAdvanced` is emitted before any `commandIssued`, and `concatMap` will not subscribe to the
 * next event's inner observable until this one completes. Together those give the property the
 * machine depends on: **by the time a Command is handled, or the next event is processed, the state
 * has already advanced.** That is what makes single-flight work — a second `accessTokenRejected`
 * arriving in the same tick is evaluated against `refreshing`, so `transition()` returns no
 * Commands and the duplicate restore never happens.
 *
 * `session.effects.spec.ts` pins it.
 *
 * ## Ordering requirement at bootstrap
 *
 * Nothing may call Amplify's `fetchAuthSession()` before Session Restore has primed the token
 * storage, or Amplify reads an empty `VaultTokenStorage`, concludes there is no session, and signs
 * the person out of one that is still good.
 */
@Injectable()
export class SessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly reporter = inject(AuthTelemetryReporter);
  private readonly clock = inject(AUTH_CLOCK);

  readonly advance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.eventReceived),
      concatLatestFrom(() => this.store.select(selectSession)),
      concatMap(([{ event }, before]): Action[] => {
        const [after, commands] = transition(before, event, this.clock());

        // Derived from the transition rather than reported by callers, so it cannot be forgotten
        // and `transition()` stays pure.
        this.reporter.record(before, after, event, commands);

        return [
          SessionActions.stateAdvanced({ state: after }),
          ...commands.map(command => SessionActions.commandIssued({ command })),
        ];
      })
    )
  );
}
